import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { trpc } from '@maxus/website/lib'

import { Avatar } from '../../base'

export type MemberSearchResult = {
  id: string
  name: string
  avatarUrl: string | null
  email: string | null
  department: string | null
}

export const MemberSearch = ({
  values,
  onSelect,
  onUnSelect,
}: {
  values: { id: string; unselectable: boolean }[]
  onSelect: (member: MemberSearchResult) => void
  onUnSelect: (id: string) => void
}) => {
  const { t } = useTranslation()

  const [memberSearch, setMemberSearch] = useState('')
  const trimmedMemberSearch = memberSearch.trim()
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('')

  useEffect(
    () => {
      if (trimmedMemberSearch.length === 0) {
        setDebouncedMemberSearch('')
        return
      }

      const timeoutId = window.setTimeout(
        () => {
          setDebouncedMemberSearch(trimmedMemberSearch)
        },
        700,
      )

      return () => window.clearTimeout(timeoutId)
    },
    [trimmedMemberSearch],
  )

  const isDebouncingMembers = trimmedMemberSearch.length > 0 && trimmedMemberSearch !== debouncedMemberSearch
  const { data: memberSearchData, isFetching: isSearchingMembers } = useQuery(
    trpc.project.user.list.queryOptions(
      {
        filter: { q: debouncedMemberSearch },
        limit: 20,
      },
      {
        enabled: debouncedMemberSearch.length > 0,
      },
    ),
  )
  const memberSearchResults =
    memberSearchData?.items.filter(item => {
      const currentValue = values.find(value => value.id === item.id)
      return !currentValue || currentValue.unselectable
    }) ?? []

  const selectMember = (member: MemberSearchResult) => {
    const currentValue = values.find(value => value.id === member.id)
    if (currentValue?.unselectable) {
      onUnSelect(member.id)
      setMemberSearch('')
      return
    }

    onSelect(member)
    setMemberSearch('')
  }

  return (
    <Command className="relative mt-2 w-full rounded-md border border-border bg-background" shouldFilter={false}>
      <Command.Input
        className="h-9 w-full bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
        onValueChange={setMemberSearch}
        placeholder={t('Search members')}
        value={memberSearch}
      />
      {trimmedMemberSearch.length > 0 && (
        <Command.List className="absolute inset-x-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-background py-1 shadow-md">
          {(isDebouncingMembers || isSearchingMembers) && (
            <Command.Loading className="px-3 py-2 text-sm text-muted-foreground">{t('Searching...')}</Command.Loading>
          )}
          {!isDebouncingMembers && !isSearchingMembers && memberSearchResults.length === 0 && (
            <Command.Empty className="px-3 py-2 text-sm text-muted-foreground">{t('No members found')}</Command.Empty>
          )}
          {!isDebouncingMembers &&
            memberSearchResults.map(member => {
              const description = member.department || member.email || ''

              return (
                <Command.Item
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm aria-selected:bg-muted"
                  key={member.id}
                  onSelect={() => selectMember(member)}
                  value={member.id}
                >
                  <Avatar avatarUrl={member.avatarUrl} classNames={{ root: 'size-7' }} name={member.name} />
                  <div className="min-w-0 flex flex-col">
                    <span className="truncate">{member.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{description}</span>
                  </div>
                </Command.Item>
              )
            })}
        </Command.List>
      )}
    </Command>
  )
}
